import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
// import { setContext } from '@apollo/link-context';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { UrlInfo } from 'aws-appsync-subscription-link/lib/types';
import { useEffect, useState } from 'react';
import { rtkGqlClient } from '../gqlBaseQuery';
import type { ConnectionQuery } from '../gqlQuery';
// import { useSelector } from 'react-redux';
// import {
//     selectAccessTokenJson,
//     selectUserSettings,
// } from '../../app/store/userSlice';
// import { Logger } from '../../common/Logger';
import { useRecoilStateLoadable } from 'recoil';
import { user } from '../../recoil/atoms/user';
import {
  checkAndGetNewAccessToken,
  getCachedAccessToken,
} from '../checkAndGetNewAccessToken';
import { getConnection } from '../getConnection';

export let apolloAppSyncGlobal:
  | {
      client: ApolloClient<NormalizedCacheObject>;
      connection: ConnectionQuery['connection'];
    }
  | undefined;

/**
 * Create apollo client using connection parameters from connection query
 */
function createClient(props: {
  /** Connection parameters */
  connection: ConnectionQuery['connection'];
  /** Retrieve accessToken */
  accessToken: string | undefined;
}) {
  // build UrlInfo for appsync sdk
  const urlInfo: UrlInfo = {
    url: props.connection.originUrl,
    region: props.connection.region,
    auth: {
      type: AUTH_TYPE.AWS_LAMBDA,
      token: props.accessToken || 'ACCESS_TOKEN_NOT_READY_IN_CLIENT',
      // jwtToken: async () => token, // Required when you use Cognito UserPools OR OpenID Connect. token object is obtained previously
      // credentials: async () => credentials, // Required when you use IAM-based auth.
    },
  };

  // create graphql link to appsync
  const httpLink = createHttpLink({
    uri: urlInfo.url,
  });

  // create hybrid link for graphql + graphql-realtime
  // with autorization (accessToken) injection and appsync apikey injection
  const link = ApolloLink.from([
    // authLink, // not needed since using lambda auth
    createAuthLink(urlInfo),
    createSubscriptionHandshakeLink(urlInfo, httpLink),
  ]);

  // Create new apollo client and copy to global state for non-hooks
  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });
}

/**
 * Custom hook that creates an appsync compliant apollo client
 * @see https://blog.purple-technology.com/lessons-learned-aws-appsync-subscriptions/
 * @see https://medium.com/swlh/how-to-really-use-aws-amplify-fcb4c5ed769c
 * @see https://docs.aws.amazon.com/appsync/latest/devguide/resolver-mapping-template-reference-programming-guide.html
 * @see https://docs.amplify.aws/lib/graphqlapi/concepts/q/platform/js/#using-aws-appsync
 * @see https://github.com/awslabs/aws-mobile-appsync-sdk-js/
 * @see https://github.com/awslabs/aws-mobile-appsync-sdk-js/blob/master/packages/aws-appsync-subscription-link/src/realtime-subscription-handshake-link.ts#L256
 * @see https://docs.aws.amazon.com/appsync/latest/devguide/appsync-dg.pdf
 * @returns AppSync client and callback that forces a reconnects to appsync (for expired accessToken or blue/green flip)
 */
export function useApolloAppSyncClient() {
  const [apolloAppSync, setApolloAppSync] = useState<{
    client: ApolloClient<NormalizedCacheObject>;
    connection: ConnectionQuery['connection'];
  }>();

  const [reconnectId, setReconnectId] = useState(0);
  const handleReconnect = () => setReconnectId((state) => state + 1);
  // const accessTokenJson = useSelector(selectAccessTokenJson);
  const [userAtom, _setUserAtom] = useRecoilStateLoadable(user);
  const userSettings: any = userAtom?.contents ?? {};
  // const userSettings = useSelector(selectUserSettings);

  useEffect(() => {
    getConnection()
      .then(async (response) => {
        if (!response) {
          throw new Error(
            'useApolloAppSyncClient.ts: Response from AppSync connection request null'
          );
        }

        const { accessToken } = await checkAndGetNewAccessToken();

        apolloAppSyncGlobal = {
          client: createClient({
            connection: response,
            accessToken,
          }),
          connection: response,
        };

        // update rtk-query with new token
        // NOTE: must be done before apollo client set so token is in sync
        // rtkGqlClient.setHeader(
        //   'authorization',
        //   getAccessToken() || 'ACCESS_TOKEN_NOT_READY_GQL_CLIENT'
        // );

        // update any components using client
        setApolloAppSync(apolloAppSyncGlobal);
      })
      .catch((exception) => {});

    // update rtk-query graphql client
    if (userSettings?.accessToken) {
      rtkGqlClient.setHeader(
        'authorization',
        userSettings?.accessToken ?? getCachedAccessToken() ?? 'ACCESS_TOKEN_NOT_READY_GQL_CLIENT' // prettier-ignore
      );
    }

    // disconnect during clean-up phase
    return () => {
      apolloAppSyncGlobal?.client.stop();
    };
  }, [reconnectId, userSettings?.accessToken]);

  return {
    /** AppSync-compliant Apollo client with subscription support */
    client: apolloAppSync?.client,
    /** Connection parameters */
    connection: apolloAppSync?.connection,
    /** Force reconnect to AppSync server for accessToken renewal or blue/green flip */
    reconnect: handleReconnect,
  };
}
